<template>
  <div>
    <div class="d-flex items-center mb-2">
      <b-button
        variant="primary"
        size="sm"
        class="mr-1 rounded-lg p-0"
        @click="backToMainPage"
      >
        <feather-icon
          size="2x"
          icon="ChevronLeftIcon"
        />
      </b-button>
      <h4 class="font-bold text-black d-inline-flex mb-0">
        Tambah Produk
      </h4>
    </div>
    <b-table
      id="table"
      ref="selectableTable"
      :items="items"
      :fields="productListFields"
      empty-text="Tidak ada data yang ditampilkan."
      responsive
      show-empty
      class="mb-0 border-b"
    >
      <template #head(checkbox)>
        <BCheckbox
          v-model="selectAll"
          @change="handleCheckAllCheckbox"
        />
      </template>
      <template #cell(checkbox)="data">
        <BCheckbox
          v-model="selected"
          :value="generateKey(data.item)"
          :disabled="data.item.stock === 0"
        />
      </template>
      <template #cell(variant)="data">
        <div
          v-if="data.item.variants"
          class="grid gap-[5px]"
        >
          <div
            v-for="item in data.item.variants"
            :key="item.variant_id"
            class="h-[40px] flex items-center"
          >
            {{ item.name }}
          </div>
        </div>
        <div
          v-else
        >
          -
        </div>
      </template>
      <template #cell(stock)="data">
        <div
          v-if="data.item.variants"
          class="grid gap-[5px]"
        >
          <div
            v-for="item in data.item.variants"
            :key="item.variant_id"
            class="h-[40px] flex items-center"
          >
            {{ item.stock }}
          </div>
        </div>
        <div
          v-else
        >
          {{ data.item.stock }}
        </div>
      </template>
      <template #cell(total)="data">
        <div
          v-if="data.item.variants"
          class="grid gap-[5px]"
        >
          <TotalItemCell
            v-for="(item, variantIndex) in data.item.variants"
            :ref="`totalItemCell-${data.index}-${variantIndex || 0}`"
            :key="item.variant_id"
            :data="data"
            :variant-index="variantIndex"
            @new-value="handleNewTotalItem($event, data.index, data.item, variantIndex)"
          />
        </div>
        <TotalItemCell
          v-else
          :ref="`totalItemCell-${data.index}-0`"
          :data="data"
          @new-value="handleNewTotalItem($event, data.index, data.item)"
        />
      </template>
      <template #cell(pengeluaran_cost)="data">
        <div
          v-if="data.item.variants"
          class="grid gap-[5px]"
        >
          <WithdrawalFeeCell
            v-for="item in data.item.variants"
            :key="item.variant_id"
            :data="getPenarikanFee(data.item.product_id, item.variant_id)"
          />
        </div>
        <WithdrawalFeeCell
          v-else
          :data="getPenarikanFee(data.item.product_id)"
        />
      </template>
    </b-table>
    <LoadMore :fetcher="fetcher" />
    <div class="sticky bottom-0 bg-white w-full px-[10px] z-[10]">
      <div class="d-flex justify-end py-2 sm:mr-[38px]">
        <div class="w-full sm:w-1/2 space-y-[12px]">
          <div class="d-flex justify-between flex-wrap gap-[10px]">
            <div class="text-[20px] font-weight-bolder text-black">
              Total Pembayaran
            </div>
            <div class="text-[20px] font-weight-bolder text-primary ">
              {{ currentTotalPayment | rupiah }}
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-end bg-white w-full py-[10px] z-[10] border-t">
        <b-button
          class="w-full sm:w-auto"
          :variant="disableSaveButton || !isEligibleToSave ? 'secondary' : 'primary'"
          :disabled="disableSaveButton || !isEligibleToSave"
          @click="addProduct"
        >
          Tambahkan Produk
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import debounce from 'lodash/debounce'
import createRecalculation from '@/views/pages/kompack/packing/composables/useRecalculation'
import TotalItemCell from './TotalItemCell.vue'
import { productListFields } from '../config'
import WithdrawalFeeCell from './WithdrawalFeeCell.vue'
import LoadMore from './LoadMore.vue'

export default {
  name: 'AddProductMenu',
  components: {
    TotalItemCell,
    WithdrawalFeeCell,
    LoadMore,
  },
  props: {
    items: {
      type: Array,
      required: false,
      default: () => [],
    },
    warehouseId: {
      type: String,
      required: false,
      default: '',
    },
    fetcher: {
      type: Object,
      required: false,
      default: () => null,
    },
  },
  data() {
    return {
      products: [],
      productListFields,
      selectAll: false,
      selected: [],
      productRepository: {},
      cancelTokens: {}, // Store cancel tokens per objKey
      disableSaveButton: true,
      totalPayment: 0,
      productHasApplied: false,
    }
  },
  computed: {
    currentTotalPayment() {
      return this.selected.reduce((acc, item) => {
        const productData = this.productRepository[item.product_id]
        if (!productData) return acc
        if (productData.is_variant) {
          const variantData = productData.variants ? Object.values(productData.variants || {}) : []
          return acc + variantData.reduce((sum, v) => sum + (v.cost || 0), 0)
        }
        return acc + productData.cost
      }, 0)
    },
    allItemIds() {
      return this.items.reduce((acc, item) => {
        if (item.stock === 0) return acc
        acc.push({
          product_name: item.product_name,
          product_id: item.product_id,
          modifier: item.percentage_sharing_fee,
          variant_ids: item.variants?.map(variant => ({
            option_id: variant.variant_id,
            variant_id: variant.variant_id,
            variant_name: variant.name,
            variant_stock: variant.stock,
            warehouse_variant_id: variant.product_warehouse_variant_id,
          })),
        })
        return acc
      }, [])
    },
    isEligibleToSave() {
      if (this.selected.length === 0) return false
      return !this.selected.some(item => {
        const productData = this.productRepository[item.product_id]

        return !productData || (productData.is_variant && Object.keys(productData.variants).length === 0)
      })
    },
    /**
     * Mapping the selected products to the payload preview.
     * The selected products is the products that are checked
     * in the table and has the total item greater than 0.
     */
    selectedProducts() {
      const mappedSelectedData = this.selected?.reduce((acc, item) => {
        const productData = this.productRepository[item.product_id]
        if (!productData) return acc
        const variantData = productData.variants ? Object.values(productData.variants || {}) : []
        return [...acc, {
          ...productData,
          modifier: item.modifier,
          total: productData.is_variant ? variantData.reduce((sum, v) => sum + (v.total || 0), 0) : productData.total,
          variants: variantData,
        }]
      }, [])
      return mappedSelectedData
    },
  },
  created() {
    this.recalculation = createRecalculation(this)

    this.previousSelected = []

    this.debouncedRecalculateMap = {}
  },
  beforeDestroy() {
    Object.values(this.debouncedRecalculateMap).forEach(debouncedFn => {
      if (debouncedFn) debouncedFn.cancel()
    })

    Object.values(this.cancelTokens).forEach(source => {
      if (source) source.cancel('Component destroyed')
    })
  },
  methods: {
    getPenarikanFee(productId, variantId) {
      const empty = { isLoading: false, cost: 0 }
      if (variantId != null && variantId !== '') {
        return this.productRepository[productId]?.variants?.[variantId] ?? empty
      }
      return this.productRepository[productId] ?? empty
    },
    /**
     * Generate key to be used as key for product repository
     */
    generateKey(item) {
      return {
        product_name: item.product_name,
        product_id: item.product_id,
        modifier: item.percentage_sharing_fee,
        variant_ids: item.variants?.map(variant => ({
          option_id: variant.variant_id,
          variant_id: variant.variant_id,
          variant_name: variant.name,
          variant_stock: variant.stock,
          warehouse_variant_id: variant.product_warehouse_variant_id,
        })),
      }
    },
    backToMainPage() {
      return this.$swal({
        title: 'Batalkan Tambah Stok Produk?',
        text: 'Data produk yang sudah kamu masukkan sebelumnya akan direset',
        icon: 'warning',
        reverseButtons: true,
        iconHtml: '<img src="https://storage.googleapis.com/komerce/core/icon-popup-warning.png">',
        showCancelButton: true,
        cancelButtonText: 'Tidak',
        confirmButtonText: 'Iya',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          icon: 'border-0 w-50 my-5',
          confirmButton: 'btn btn-primary px-4',
          cancelButton: 'btn btn-outline-primary mr-1 px-5',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.selected = this.previousSelected
          this.$emit('backToMainPage')
        }
      })
    },
    /**
     * Handle check all displayed items
     */
    handleCheckAllCheckbox(value) {
      if (value) {
        this.selected = this.allItemIds
      } else {
        this.selected = []
      }
    },
    /**
     * Mapping the new data to the product repository and recalculate the storage cost
     */
    handleNewTotalItem(newTotalValue, index, item, variantIndex) {
      const productId = item.product_id
      let variant = null
      if (variantIndex !== undefined) {
        variant = item.variants[variantIndex]
      }

      this.disableSaveButton = true

      const previousRepositoryData = this.productRepository[productId]
      let originalTotalValue = 0
      const isVariant = variantIndex !== undefined && item.variants?.length > 0

      if (!previousRepositoryData && !isVariant) {
        this.$set(this.productRepository, productId, {
          id: productId,
          product_name: item.product_name,
          is_variant: false,
          product_id: productId,
          stock: item.stock,
          variants: {},
          cost: 0,
          total: 0,
          isLoading: true,
        })
      }

      if (!previousRepositoryData && isVariant) {
        const newVariantData = {
          warehouse_variant_id: variant.product_warehouse_variant_id,
          variant_name: variant.name,
          option_id: variant.variant_id,
          variant_stock: variant.stock,
          cost: 0,
          total: 0,
          isLoading: true,
        }
        this.$set(this.productRepository, productId, {
          product_name: item.product_name,
          is_variant: true,
          product_id: productId,
          stock: item.stock,
          variants: {
            [variant.variant_id]: newVariantData,
          },
        })
      }

      if (previousRepositoryData && !isVariant) {
        this.$set(this.productRepository[productId], 'isLoading', true)
        originalTotalValue = previousRepositoryData.total
      }

      if (previousRepositoryData && isVariant) {
        if (!previousRepositoryData.variants[variant.variant_id]) {
          this.$set(this.productRepository[productId].variants, variant.variant_id, {
            warehouse_variant_id: variant.product_warehouse_variant_id,
            variant_name: variant.name,
            option_id: variant.variant_id,
            variant_stock: variant.stock,
            cost: 0,
          })
        }

        this.$set(this.productRepository[productId].variants[variant.variant_id], 'isLoading', true)
        originalTotalValue = previousRepositoryData.variants[variant.variant_id].total
      }

      this.doRecalculate({
        originalTotalValue,
        newTotalValue,
        productId,
        variantId: variant?.variant_id,
        index,
        variantIndex,
        item,
      })
    },
    /**
     * Do the recalculation of the fulfillment cost of the relevant product with ability to cancel
     * the request if the same product is recalculated on the same time.
     * This function is debounced to prevent multiple requests.
     * @param data {Object} - The data to recalculate the fulfillment cost.
     */
    doRecalculate(data) {
      const debounceKey = data.variantId ? `${data.productId}-${data.variantId}` : `${data.productId}`

      if (!this.debouncedRecalculateMap[debounceKey]) {
        this.debouncedRecalculateMap[debounceKey] = debounce(this.recalculate, 500)
      }

      this.debouncedRecalculateMap[debounceKey](data)
    },
    /**
     * Recalculate the storage cost of the relevant product with ability to cancel
     * the request if the same product is recalculated on the same time.
     * This function is debounced to prevent multiple requests.
     */
    async recalculate({
      originalTotalValue,
      newTotalValue,
      productId,
      variantId,
      index,
      variantIndex,
      item,
    }) {
      const cancelKey = variantId ? `${productId}-${variantId}` : productId

      if (this.cancelTokens[cancelKey]) {
        this.cancelTokens[cancelKey].cancel('New request for same product')
      }

      if (newTotalValue === 0) {
        if (variantId) {
          this.$delete(this.productRepository[productId].variants, variantId)
        } else {
          this.$delete(this.productRepository, productId)
        }

        this.disableSaveButton = false
        return
      }

      this.cancelTokens[cancelKey] = axios.CancelToken.source()

      const products = [{
        product_id: productId,
        total_item: newTotalValue,
        is_product_bonus: false,
        is_bundling: false,
        bundle_components: null,
        storage_type: item?.storage_type,
      }]

      const body = {
        warehouse_id: Number(this.warehouseId),
        total_order: 1,
        packings: [],
        products,
      }

      try {
        const { data } = await this.$http_new.post(
          '/komship/api/v2/orders/recalculate-fulfillment',
          body,
          { cancelToken: this.cancelTokens[cancelKey].token },
        )

        if (variantId) {
          this.$set(this.productRepository[productId].variants[variantId], 'cost', data.data.grand_fulfillment_cost)
          this.$set(this.productRepository[productId].variants[variantId], 'fulfillment_cost', data.data.grand_fulfillment_cost)
          this.$set(this.productRepository[productId].variants[variantId], 'total', newTotalValue)
          this.$set(this.productRepository[productId].variants[variantId], 'isLoading', false)
        } else {
          this.$set(this.productRepository[productId], 'cost', data.data.grand_fulfillment_cost)
          this.$set(this.productRepository[productId], 'fulfillment_cost', data.data.grand_fulfillment_cost)
          this.$set(this.productRepository[productId], 'total', newTotalValue)
          this.$set(this.productRepository[productId], 'isLoading', false)
        }

        delete this.cancelTokens[cancelKey]
      } catch (error) {
        if (axios.isCancel(error)) {
          return
        }

        this.$refs[`totalItemCell-${index}-${variantIndex || 0}`].setTotalValue(originalTotalValue)

        if (!variantId) {
          this.$set(this.productRepository[productId], 'isLoading', false)
        }

        if (variantId) {
          this.$set(this.productRepository[productId].variants[variantId], 'isLoading', false)
        }

        this.$toast_error({ message: 'Gagal melakukan rekalkulasi. Silakan coba lagi' })
      } finally {
        this.disableSaveButton = false
      }
    },
    /**
     * Send selected products to parent component
     */
    addProduct() {
      const { selectedProducts } = this
      if (selectedProducts.length > 0) {
        this.productHasApplied = true
      }
      this.$emit('addProduct', selectedProducts, this.currentTotalPayment, this.selected, this.productRepository)
    },
    setPreviousData(selectedData, totalPayment) {
      this.previousSelected = selectedData
      this.totalPayment = totalPayment
    },
  },
}
</script>
